import React, { useState, useEffect, useRef } from 'react';
import { productService, categoryService, mediaService } from '../../services/api';
import { X, Pencil, Trash2 } from 'lucide-react';

function ProductsManagement() {
  const productsRef = useRef([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantForm, setVariantForm] = useState({ name: '', image: '', sku: '', price: '', discount_price: '', stock: '', attributes: '' });
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [uploadingVariantImage, setUploadingVariantImage] = useState(false);
  const [offerForm, setOfferForm] = useState({ title: '', type: 'percentage', value: '', description: '' });
  const [tagName, setTagName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '', // MRP
    discount_price: '', // Selling Price
    category_id: '',
    stock: '',
    sku: '',
    featured_image: '',
    is_active: true,
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        productService.index(),
        categoryService.index()
      ]);
      setProducts(Array.isArray(productsData) ? productsData : productsData?.data || []);
      productsRef.current = Array.isArray(productsData) ? productsData : productsData?.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : categoriesData?.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await productService.update(editingProduct.id, formData);
      } else {
        await productService.store(formData);
      }
      setShowModal(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', price: '', discount_price: '', category_id: '', stock: '', sku: '', featured_image: '', is_active: true });
      loadData();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      discount_price: product.discount_price || '',
      category_id: product.category?.id || product.category_id,
      stock: product.stock,
      sku: product.sku,
      featured_image: product.featured_image || '',
      is_active: product.is_active ?? true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.destroy(id);
        loadData();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    try {
      await productService.storeVariant(selectedProduct.id, {
        name: variantForm.name,
        image: variantForm.image || null,
        sku: variantForm.sku,
        price: variantForm.price || null,
        discount_price: variantForm.discount_price || null,
        stock: variantForm.stock || 0,
        attributes: variantForm.attributes ? JSON.parse(variantForm.attributes) : null,
      });
      setVariantForm({ name: '', image: '', sku: '', price: '', discount_price: '', stock: '', attributes: '' });
      await loadData();
      const updated = productsRef.current.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
    } catch (error) {
      alert('Failed to add variant: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditVariant = (variant) => {
    setEditingVariantId(variant.id);
    setVariantForm({
      name: variant.name || '',
      image: variant.image || '',
      sku: variant.sku || '',
      price: variant.price || '',
      discount_price: variant.discount_price || '',
      stock: variant.stock !== undefined ? String(variant.stock) : '',
      attributes: variant.attributes ? JSON.stringify(variant.attributes) : '',
    });
  };

  const handleCancelEditVariant = () => {
    setEditingVariantId(null);
    setVariantForm({ name: '', image: '', sku: '', price: '', discount_price: '', stock: '', attributes: '' });
  };

  const handleUpdateVariant = async (e) => {
    e.preventDefault();
    try {
      await productService.updateVariant(selectedProduct.id, editingVariantId, {
        name: variantForm.name,
        image: variantForm.image || null,
        sku: variantForm.sku,
        price: variantForm.price || null,
        discount_price: variantForm.discount_price || null,
        stock: parseInt(variantForm.stock) || 0,
        attributes: variantForm.attributes ? JSON.parse(variantForm.attributes) : null,
      });
      setVariantForm({ name: '', image: '', sku: '', price: '', discount_price: '', stock: '', attributes: '' });
      setEditingVariantId(null);
      await loadData();
      const updated = productsRef.current.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
    } catch (error) {
      alert('Failed to update variant: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Are you sure you want to delete this variant?')) return;
    try {
      await productService.destroyVariant(selectedProduct.id, variantId);
      await loadData();
      const updated = productsRef.current.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
    } catch (error) {
      alert('Failed to delete variant: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddOffer = async (e) => {
    e.preventDefault();
    try {
      await productService.storeOffer(selectedProduct.id, offerForm);
      setOfferForm({ title: '', type: 'percentage', value: '', description: '' });
      await loadData();
      const updated = productsRef.current.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
    } catch (error) {
      alert('Failed to add offer: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    try {
      const tag = await productService.storeTag(tagName);
      await productService.attachTags(selectedProduct.id, [...(selectedProduct.tags || []).map((item) => item.id), tag.id]);
      setTagName('');
      await loadData();
      const updated = productsRef.current.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
    } catch (error) {
      alert('Failed to add tag: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Products Management</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Product
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variants</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => (
              <tr key={product.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {product.featured_image ? (
                    <img src={product.featured_image} alt={product.name} className="h-12 w-12 rounded object-cover" />
                  ) : (
                    <div className="h-12 w-12 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">No Img</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{product.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">{product.sku}</td>
                <td className="px-6 py-4 whitespace-nowrap">₹{product.price}</td>
                <td className="px-6 py-4 whitespace-nowrap">{product.stock}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {product.variants && product.variants.length > 0 ? (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">{product.variants.length} variant{product.variants.length > 1 ? 's' : ''}</span>
                  ) : (
                    <span className="text-gray-400 text-xs">None</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{product.category?.name || product.category_id}</td>
                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                  <button
                    onClick={() => handleEdit(product)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedProduct(product)}
                    className="text-purple-600 hover:text-purple-900"
                  >
                    Advanced
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && (
          <div className="text-center py-8 text-gray-500">No products found</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProduct(null);
                  setFormData({ name: '', description: '', price: '', discount_price: '', category_id: '', stock: '', sku: '', featured_image: '', is_active: true });
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Product Name</label>
                    <input
                      type="text"
                      required
                      className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">SKU</label>
                    <input
                      type="text"
                      required
                      className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                    <textarea
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">MRP (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Selling Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                        value={formData.discount_price}
                        onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
                      />
                      <p className="text-[10px] text-gray-500 mt-1">Leave blank if same as MRP</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Stock Quantity</label>
                    <input
                      type="number"
                      required
                      className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
                    <select
                      required
                      className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Product Image</label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="text"
                        placeholder="Image URL"
                        className="flex-1 border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition"
                        value={formData.featured_image}
                        onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setUploadingImage(true);
                            try {
                              const media = await mediaService.upload(file, 'products');
                              let url = media.url || media.path || '';
                              if (url && !url.startsWith('http')) {
                                const base = (process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'https://api.ortusfnb.com').replace(/\/$/, '');
                                url = base + '/' + url.replace(/^\//, '');
                              }
                              setFormData((prev) => ({ ...prev, featured_image: url }));
                            } catch (err) {
                              alert('Upload failed: ' + (err.response?.data?.message || err.message));
                            }
                            setUploadingImage(false);
                          }}
                        />
                        <span className={`inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-bold transition ${uploadingImage ? 'bg-gray-300 text-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'}`}>
                          {uploadingImage ? 'Uploading...' : 'Upload'}
                        </span>
                      </label>
                    </div>
                    {formData.featured_image && (
                      <div className="mt-3 p-2 border border-dashed border-gray-200 rounded-lg">
                        <img src={formData.featured_image} alt="Preview" className="h-32 w-full object-contain" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-bold text-gray-700 group-hover:text-gray-900 transition">Product is Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    setFormData({ name: '', description: '', price: '', discount_price: '', category_id: '', stock: '', sku: '', featured_image: '', is_active: true });
                  }}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Advanced Product: {selectedProduct.name}</h3>
              <button onClick={() => setSelectedProduct(null)} className="text-gray-500">Close</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Existing Variants */}
              <div className="space-y-3 border rounded p-4">
                <h4 className="font-semibold">Existing Variants</h4>
                {selectedProduct.variants && selectedProduct.variants.length > 0 ? (
                  <div className="space-y-2">
                    {selectedProduct.variants.map((v) => (
                      <div key={v.id} className={`bg-gray-50 rounded-lg p-3 text-sm ${editingVariantId === v.id ? 'ring-2 ring-blue-400' : ''}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-gray-900">{v.name}</div>
                            <div className="text-xs text-gray-500">SKU: {v.sku}</div>
                            <div className="text-xs text-gray-500">Stock: {v.stock}</div>
                            {v.attributes && Object.keys(v.attributes).length > 0 && (
                              <div className="text-xs text-gray-400 mt-1">
                                {Object.entries(v.attributes).map(([k, val]) => `${k}: ${val}`).join(', ')}
                              </div>
                            )}
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="text-right">
                              <div className="font-bold text-gray-900">₹{v.discount_price || v.price || '—'}</div>
                              {v.discount_price && v.price && (
                                <div className="text-xs text-gray-400 line-through">₹{v.price}</div>
                              )}
                            </div>
                            <button onClick={() => handleEditVariant(v)} className="text-blue-600 hover:bg-blue-100 p-1 rounded transition" title="Edit">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => handleDeleteVariant(v.id)} className="text-red-600 hover:bg-red-100 p-1 rounded transition" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No variants yet. Add one →</p>
                )}
              </div>

              {/* Add/Edit Variant Form */}
              <form onSubmit={editingVariantId ? handleUpdateVariant : handleAddVariant} className="space-y-3 border rounded p-4">
                <h4 className="font-semibold">{editingVariantId ? 'Edit Variant' : 'Add Variant'}</h4>
                <input required placeholder="Variant name (e.g. 500g, 1kg)" value={variantForm.name} onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })} className="w-full border rounded p-2" />
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Variant Image (shown when selected)</label>
                  <div className="flex items-center gap-3">
                    {variantForm.image && (
                      <img src={variantForm.image} alt="Variant" className="w-12 h-12 rounded-lg object-cover border" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingVariantImage(true);
                        try {
                          const media = await mediaService.upload(file, 'variants');
                          let url = media.url || media.path || '';
                          if (url && !url.startsWith('http')) {
                            const base = (process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'https://api.ortusfnb.com').replace(/\/$/, '');
                            url = base + '/' + url.replace(/^\//, '');
                          }
                          setVariantForm({ ...variantForm, image: url });
                        } catch (err) {
                          alert('Failed to upload image');
                        } finally {
                          setUploadingVariantImage(false);
                        }
                      }}
                      className="text-sm"
                    />
                    {uploadingVariantImage && <span className="text-xs text-blue-600">Uploading...</span>}
                    {variantForm.image && (
                      <button type="button" onClick={() => setVariantForm({ ...variantForm, image: '' })} className="text-red-600 text-xs hover:underline">Remove</button>
                    )}
                  </div>
                </div>
                <input required placeholder="Variant SKU" value={variantForm.sku} onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })} className="w-full border rounded p-2" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" placeholder="MRP Price" value={variantForm.price} onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })} className="w-full border rounded p-2" />
                  <input type="number" step="0.01" placeholder="Sell Price" value={variantForm.discount_price} onChange={(e) => setVariantForm({ ...variantForm, discount_price: e.target.value })} className="w-full border rounded p-2" />
                </div>
                <input type="number" placeholder="Stock" value={variantForm.stock} onChange={(e) => setVariantForm({ ...variantForm, stock: e.target.value })} className="w-full border rounded p-2" />
                <textarea placeholder='Attributes JSON e.g. {"size":"500g"}' value={variantForm.attributes} onChange={(e) => setVariantForm({ ...variantForm, attributes: e.target.value })} className="w-full border rounded p-2" />
                <div className="flex gap-2">
                  <button type="submit" className={`flex-1 text-white px-4 py-2 rounded ${editingVariantId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {editingVariantId ? 'Update Variant' : 'Save Variant'}
                  </button>
                  {editingVariantId && (
                    <button type="button" onClick={handleCancelEditVariant} className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              <form onSubmit={handleAddOffer} className="space-y-3 border rounded p-4">
                <h4 className="font-semibold">Add Offer</h4>
                <input required placeholder="Offer title" value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} className="w-full border rounded p-2" />
                <select value={offerForm.type} onChange={(e) => setOfferForm({ ...offerForm, type: e.target.value })} className="w-full border rounded p-2">
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </select>
                <input required type="number" step="0.01" placeholder="Value" value={offerForm.value} onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })} className="w-full border rounded p-2" />
                <textarea placeholder="Description" value={offerForm.description} onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} className="w-full border rounded p-2" />
                <button className="bg-green-600 text-white px-4 py-2 rounded">Save Offer</button>
              </form>

              <form onSubmit={handleAddTag} className="space-y-3 border rounded p-4">
                <h4 className="font-semibold">Add Tag</h4>
                <input required placeholder="Tag name" value={tagName} onChange={(e) => setTagName(e.target.value)} className="w-full border rounded p-2" />
                <button className="bg-purple-600 text-white px-4 py-2 rounded">Create & Attach Tag</button>
                <div className="text-sm text-gray-600">
                  Current tags: {(selectedProduct.tags || []).map((tag) => tag.name).join(', ') || 'None'}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsManagement;
